// src/components/admin/TorreDeControl/SearchBar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Search, X, MapPin, Package } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

function SearchBar({ isDeliveryMode, onResultClick }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(async () => {
      if (searchTerm.trim().length > 0) {
        await performSearch(searchTerm);
      } else {
        setResults([]);
        setIsOpen(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm, isDeliveryMode]);

  async function performSearch(term) {
    setIsLoading(true);
    try {
      const endpoint = isDeliveryMode
        ? `${API_URL}/api/admin/search-deliveries`
        : `${API_URL}/api/admin/search-orders`;

      const response = await axios.get(endpoint, {
        params: { q: term, limit: 5 },
      });

      setResults(response.data.results || []);
      setIsOpen(true);
    } catch (error) {
      console.error('Error searching:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }

  function handleResultClick(id) {
    onResultClick(id, isDeliveryMode ? 'delivery' : 'order');
    setSearchTerm('');
    setResults([]);
    setIsOpen(false);
  }

  function clearSearch() {
    setSearchTerm('');
    setResults([]);
    setIsOpen(false);
  }

  return (
    <div ref={searchRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={`Buscar ${isDeliveryMode ? 'entrega' : 'recojo'}...`}
          className="w-64 pl-9 pr-8 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E] focus:border-transparent"
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        {searchTerm && (
          <button
            onClick={clearSearch}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute top-full mt-2 w-full bg-white border border-slate-200 rounded-lg shadow-lg z-[60] max-h-80 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-slate-500 text-sm">
              Buscando...
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-slate-500 text-sm">
              No se encontraron resultados
            </div>
          ) : (
            <div className="py-2">
              {results.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleResultClick(item.id)}
                  className="w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      {isDeliveryMode ? (
                        <MapPin className="w-4 h-4 text-[#B1336E]" />
                      ) : (
                        <Package className="w-4 h-4 text-[#B1336E]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-[#B1336E] text-sm">
                          #{item.id}
                        </span>
                        {item.status && (
                          <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                            {item.status}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-slate-700 font-medium truncate">
                        {item.name}
                      </div>
                      {item.phone && (
                        <div className="text-xs text-slate-500 mt-0.5">
                          {item.phone}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchBar;